import React, { useState } from 'react';
import { SearchIcon, FilterIcon, ArrowRightIcon, CheckCircleIcon, XCircleIcon, ClockIcon, RotateCcwIcon } from 'lucide-react';
import { Transaction } from '../../utils/dashboardData';
interface RecentTransactionsProps {
  data: Transaction[];
}
const RecentTransactions: React.FC<RecentTransactionsProps> = ({
  data
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const getSchemeLogoUrl = (name: string) => {
    switch (name) {
      case 'Visa':
        return 'https://cdn.magicpatterns.com/uploads/4AYK289tAoefs5UKMeCGRu/Visa_Logo.png';
      case 'MasterCard':
        return 'https://cdn.magicpatterns.com/uploads/1vYo3uhYQcpE27Haf8Bixu/MasterCard_Logo.png';
      case 'UnionPay':
        return 'https://cdn.magicpatterns.com/uploads/mkpVZ2yX93dtJTCkJzVjji/UnionPay_Logo.png';
      case 'PayPak':
        return 'https://cdn.magicpatterns.com/uploads/fztxH1Zm6zdRKvuAw79Xb6/PayPak_Logo.png';
      case 'RAAST':
        return 'https://cdn.magicpatterns.com/uploads/nYL4tGVTuPASUdRtomRefU/RAAST_Logo.png';
      default:
        return '';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Success':
        return <CheckCircleIcon className="h-4 w-4 text-emerald-500 mr-1.5" data-id="element-153" />;
      case 'Failed':
        return <XCircleIcon className="h-4 w-4 text-red-500 mr-1.5" data-id="element-154" />;
      case 'Pending':
        return <ClockIcon className="h-4 w-4 text-amber-500 mr-1.5" data-id="element-155" />;
      case 'Reversed':
        return <RotateCcwIcon className="h-4 w-4 text-slate-500 mr-1.5" data-id="element-156" />;
      default:
        return null;
    }
  };
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Success':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Failed':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'Pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Reversed':
        return 'bg-slate-100 text-slate-700 border-slate-300';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };
  const filteredData = data.filter(tx => tx.id.toLowerCase().includes(searchTerm.toLowerCase()) || tx.terminalId.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-0 overflow-hidden" data-id="element-157">
      <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-158">
        <div data-id="element-159">
          <h2 className="text-lg font-bold text-slate-900" data-id="element-160">
            Recent Transactions
          </h2>
          <p className="text-sm text-slate-500 font-medium mt-1" data-id="element-161">
            Latest payment activity across all channels
          </p>
        </div>

        <div className="flex items-center space-x-3" data-id="element-162">
          <div className="relative" data-id="element-163">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-164">
              <SearchIcon className="h-4 w-4 text-slate-400" data-id="element-165" />
            </div>
            <input type="text" placeholder="Search ID or Terminal..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 w-full sm:w-64 bg-slate-50 focus:bg-white transition-all" data-id="element-166" />
          </div>
          <button className="p-2 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:text-indigo-600 transition-colors" title="Filter" data-id="element-167">
            <FilterIcon className="h-4 w-4" data-id="element-168" />
          </button>
          <a href="/transaction-history" className="hidden sm:flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors whitespace-nowrap bg-indigo-50 hover:bg-indigo-100 px-3 py-2 rounded-xl" data-id="element-169">
            View All <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-170" />
          </a>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-171">
        <table className="w-full text-sm text-left" data-id="element-172">
          <thead className="text-xs text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200" data-id="element-173">
            <tr data-id="element-174">
              <th className="px-6 py-4 font-semibold" data-id="element-175">Transaction ID</th>
              <th className="px-6 py-4 font-semibold" data-id="element-176">Date & Time</th>
              <th className="px-6 py-4 font-semibold" data-id="element-177">Channel</th>
              <th className="px-6 py-4 font-semibold" data-id="element-178">Scheme</th>
              <th className="px-6 py-4 font-semibold" data-id="element-179">Terminal</th>
              <th className="px-6 py-4 font-semibold text-right" data-id="element-180">Amount</th>
              <th className="px-6 py-4 font-semibold" data-id="element-181">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-182">
            {filteredData.length > 0 ? filteredData.map(tx => <tr key={tx.id} className="even:bg-slate-50/50 hover:bg-slate-50 transition-colors" data-id="element-183">
                  <td className="px-6 py-4 font-bold text-indigo-900" data-id="element-184">
                    {tx.id}
                  </td>
                  <td className="px-6 py-4 text-slate-500 font-medium" data-id="element-185">
                    {tx.dateTime}
                  </td>
                  <td className="px-6 py-4 font-semibold text-slate-700" data-id="element-186">
                    {tx.channel}
                  </td>
                  <td className="px-6 py-4" data-id="element-187">
                    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-white border border-slate-200 shadow-sm text-xs font-bold text-slate-700" data-id="element-188">
                      {getSchemeLogoUrl(tx.scheme) && <img src={getSchemeLogoUrl(tx.scheme)} alt={tx.scheme} className="h-4 w-auto object-contain" data-id="element-189" />}
                      {tx.scheme}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-500 font-medium" data-id="element-190">
                    {tx.terminalId}
                  </td>
                  <td className="px-6 py-4 text-right font-bold text-slate-900" data-id="element-191">
                    ₨{tx.amount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4" data-id="element-192">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold border ${getStatusClass(tx.status)}`} data-id="element-193">
                      {getStatusIcon(tx.status)}
                      {tx.status}
                    </span>
                  </td>
                </tr>) : <tr data-id="element-194">
                <td colSpan={7} className="px-6 py-12 text-center" data-id="element-195">
                  <div className="flex flex-col items-center justify-center" data-id="element-196">
                    <SearchIcon className="h-8 w-8 text-slate-300 mb-3" data-id="element-197" />
                    <p className="text-slate-500 font-medium" data-id="element-198">
                      No transactions found matching your search.
                    </p>
                  </div>
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      <div className="p-4 border-t border-slate-100 sm:hidden flex justify-center bg-slate-50" data-id="element-199">
        <a href="/transaction-history" className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors flex items-center" data-id="element-200">
          View All Transactions <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-201" />
        </a>
      </div>
    </div>;
};
export default RecentTransactions;